/**
 * ZILURA wishlist, kept in this browser (localStorage, list of product handles, newest first).
 * - Any [data-zl-wish="<handle>"] button toggles that product (cards, product page, quick view).
 * - [data-zl-wish-count] elements show the number of saved products (header heart).
 * - The wishlist page (templates/collection.wishlist.json → /collections/all?view=wishlist) has
 *   [data-zl-wl-grid]; each product card is fetched from /products/<handle>?section_id=zilura-wishlist-card
 *   so it looks and works exactly like the collection grid.
 */
(function () {
  if (window.zlWishlist) return;

  const KEY = 'zilura:wishlist';
  const MAX = 100;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const read = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) {
      return [];
    }
  };

  const write = (items) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      /* private mode / storage full: the list still works for this page view */
    }
  };

  let list = read();

  const has = (handle) => list.includes(handle);

  const sync = (scope = document) => {
    scope.querySelectorAll('[data-zl-wish]').forEach((btn) => {
      const on = has(btn.dataset.zlWish);
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', String(on));
      const name = btn.dataset.zlWishName;
      if (name) btn.setAttribute('aria-label', on ? `Remove ${name} from wishlist` : `Add ${name} to wishlist`);
    });
    document.querySelectorAll('[data-zl-wish-count]').forEach((el) => {
      el.textContent = String(list.length);
      el.hidden = list.length === 0;
    });
  };

  const set = (items, changed) => {
    list = items.slice(0, MAX);
    write(list);
    sync();
    document.dispatchEvent(
      new CustomEvent('zl:wishlist-change', { detail: { handle: changed, added: has(changed), list: list.slice() } })
    );
  };

  const toggle = (handle) => set(has(handle) ? list.filter((h) => h !== handle) : [handle, ...list], handle);
  const remove = (handle) => has(handle) && set(list.filter((h) => h !== handle), handle);

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-zl-wish]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    toggle(btn.dataset.zlWish);
    btn.classList.remove('is-pop');
    void btn.offsetWidth;
    btn.classList.add('is-pop');
  });

  // Other tabs
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    list = read();
    sync();
  });

  // Cards that arrive later (filters, sorting, recommendations, quick view)
  let pending;
  new MutationObserver((records) => {
    if (!records.some((r) => r.addedNodes.length)) return;
    window.clearTimeout(pending);
    pending = window.setTimeout(() => sync(), 60);
  }).observe(document.body, { childList: true, subtree: true });

  /* ---------- wishlist page ---------- */
  const page = document.querySelector('[data-zl-wl]');
  if (page) {
    const grid = page.querySelector('[data-zl-wl-grid]');
    const empty = page.querySelector('[data-zl-wl-empty]');
    const status = page.querySelector('[data-zl-wl-status]');

    const showState = () => {
      const count = grid.children.length;
      empty.hidden = count > 0;
      grid.hidden = count === 0;
      status.textContent = count ? `${count} saved ${count === 1 ? 'piece' : 'pieces'}` : '';
    };

    const fetchCard = (handle) =>
      fetch(`${root}products/${encodeURIComponent(handle)}?section_id=zilura-wishlist-card`)
        .then((r) => (r.ok ? r.text() : null))
        .then((text) => {
          if (!text) return null;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const card = doc.querySelector('.zl-wl-card');
          return card ? card.innerHTML.trim() : null;
        })
        .catch(() => undefined);

    const render = async () => {
      page.classList.add('is-loading');
      const handles = list.slice();
      const cards = await Promise.all(handles.map(fetchCard));
      grid.innerHTML = '';
      cards.forEach((html, i) => {
        if (html === null) {
          // Product no longer exists / unpublished
          remove(handles[i]);
          return;
        }
        if (!html) return;
        const li = document.createElement('li');
        li.className = 'grid__item scroll-trigger animate--slide-in';
        li.dataset.handle = handles[i];
        li.innerHTML = html;
        grid.appendChild(li);
      });
      page.classList.remove('is-loading');
      page.classList.add('is-ready');
      sync(grid);
      showState();
    };

    document.addEventListener('zl:wishlist-change', (event) => {
      const { handle, added } = event.detail;
      if (added) return render();
      const li = grid.querySelector(`li[data-handle="${CSS.escape(handle)}"]`);
      if (li) li.remove();
      showState();
    });

    render();
  }

  sync();
  window.zlWishlist = { has, toggle, remove, sync, list: () => list.slice() };
})();
